import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import API_BASE_URL from "../Config";
import { CiHome } from "react-icons/ci";
import { IoIosTrendingUp, IoIosTrendingDown } from "react-icons/io";
import { IoMdRemove } from "react-icons/io";
import "./styles/Dashboard.css";
import "../styles/global.css";

import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

/* ─── SVG Icon System ─── */
const Icon = ({ d, size = 18, stroke = 1.8, fill = "none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
    strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}>
    {d}
  </svg>
);
const ShoppingCartIcon = (p) => <Icon {...p} d={<><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></>}/>;
const CreditCardIcon   = (p) => <Icon {...p} d={<><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></>}/>;
const UsersIcon        = (p) => <Icon {...p} d={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}/>;
const StethoscopeIcon  = (p) => <Icon {...p} d={<><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/><path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4"/><circle cx="20" cy="10" r="2"/></>}/>;
const VideoIcon        = (p) => <Icon {...p} d={<><path d="m22 8-6 4 6 4V8z"/><rect x="2" y="6" width="14" height="12" rx="2"/></>}/>;
const PackageIcon      = (p) => <Icon {...p} d={<><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></>}/>;
const ArrowRightIcon   = (p) => <Icon {...p} d={<><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>}/>;
const CalendarIcon     = (p) => <Icon {...p} d={<><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>}/>;
const ActivityIcon     = (p) => <Icon {...p} d={<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>}/>;
const TrendingUpIcon   = (p) => <Icon {...p} d={<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>}/>;
const DollarSignIcon   = (p) => <Icon {...p} d={<><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>}/>;
const ClipboardIcon    = (p) => <Icon {...p} d={<><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></>}/>;
const PhoneIcon        = (p) => <Icon {...p} d={<><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.86-1.86a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></>}/>;

/* ─── Period Filter Buttons ─── */
const PeriodFilter = ({ value, onChange }) => (
  <div className="dash-period-filter">
    {["day","weekly","monthly","yearly"].map(p => (
      <button key={p} className={`dash-period-btn ${value === p ? "active" : ""}`}
        onClick={() => onChange(p)}>
        {p.charAt(0).toUpperCase() + p.slice(1)}
      </button>
    ))}
  </div>
);

/* ─── Stat Card ─── */
const StatCard = ({ title, value, subtitle, icon, color, link, trend, trendValue, prefix = "₹" }) => (
  <div className="dash-stat-card" style={{ "--card-accent": color }}>
    <div className="dash-stat-card__top">
      <div>
        <p className="dash-stat-card__title">{title}</p>
        <h3 className="dash-stat-card__value">{prefix}{value ?? "—"}</h3>
        {subtitle && <p className="dash-stat-card__sub">{subtitle}</p>}
      </div>
      <div className="dash-stat-card__icon-wrap">{icon}</div>
    </div>
    <div className="dash-stat-card__bottom">
      <span className={`dash-stat-trend ${trend === "Increase" ? "up" : trend === "Decrease" ? "down" : "flat"}`}>
        {trend === "Increase" ? <IoIosTrendingUp /> : trend === "Decrease" ? <IoIosTrendingDown /> : <IoMdRemove />}
        {trendValue ?? 0}%
      </span>
      <span className="dash-stat-trend-label">vs last period</span>
      {link && (
        <Link to={link} className="dash-stat-link">
          View <ArrowRightIcon size={13} />
        </Link>
      )}
    </div>
  </div>
);

/* ─── Chart Card wrapper ─── */
const ChartCard = ({ title, subtitle, children, action }) => (
  <div className="dash-chart-card">
    <div className="dash-chart-card__header">
      <div>
        <h4 className="dash-chart-card__title">{title}</h4>
        {subtitle && <p className="dash-chart-card__sub">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="dash-chart-card__body">{children}</div>
  </div>
);

/* ─── Custom Tooltip ─── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="dash-tooltip">
      <p className="dash-tooltip__label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="dash-tooltip__val">
          {p.name}: ₹{p.value?.toLocaleString?.() ?? p.value}
        </p>
      ))}
    </div>
  );
};

/* ─── Pie custom label ─── */
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return percent > 0.05 ? (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {(percent * 100).toFixed(0)}%
    </text>
  ) : null;
};

/* ─── Date helpers ─── */
const getDateRange = (period) => {
  const now = new Date();
  const start = new Date();
  if (period === "day") {
    start.setHours(0, 0, 0, 0);
  } else if (period === "weekly") {
    const day = now.getDay();
    start.setDate(now.getDate() - day);
    start.setHours(0, 0, 0, 0);
  } else if (period === "monthly") {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  } else if (period === "yearly") {
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
  }
  return { start, end: now };
};

const getPrevDateRange = (period) => {
  const now = new Date();
  const prevEnd = new Date();
  const prevStart = new Date();
  if (period === "day") {
    prevEnd.setDate(now.getDate() - 1);
    prevEnd.setHours(23, 59, 59, 999);
    prevStart.setDate(now.getDate() - 1);
    prevStart.setHours(0, 0, 0, 0);
  } else if (period === "weekly") {
    const day = now.getDay();
    prevEnd.setDate(now.getDate() - day - 1);
    prevEnd.setHours(23, 59, 59, 999);
    prevStart.setDate(now.getDate() - day - 7);
    prevStart.setHours(0, 0, 0, 0);
  } else if (period === "monthly") {
    prevEnd.setDate(0);
    prevEnd.setHours(23, 59, 59, 999);
    prevStart.setDate(0);
    prevStart.setDate(1);
    prevStart.setHours(0, 0, 0, 0);
  } else if (period === "yearly") {
    prevEnd.setFullYear(now.getFullYear() - 1, 11, 31);
    prevEnd.setHours(23, 59, 59, 999);
    prevStart.setFullYear(now.getFullYear() - 1, 0, 1);
    prevStart.setHours(0, 0, 0, 0);
  }
  return { start: prevStart, end: prevEnd };
};

const parseOrderDate = (dateStr) => {
  if (!dateStr) return null;
  // Try various formats: "YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", ISO string
  const iso = new Date(dateStr);
  if (!isNaN(iso)) return iso;
  // Try DD-MM-YYYY
  const parts = dateStr.split(/[-/]/);
  if (parts.length === 3) {
    const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    if (!isNaN(d)) return d;
  }
  return null;
};

const filterByPeriod = (orders, period) => {
  const { start, end } = getDateRange(period);
  return orders.filter(o => {
    const d = parseOrderDate(o.orderdate || o.created_at);
    if (!d) return false;
    return d >= start && d <= end;
  });
};

const filterByPrevPeriod = (orders, period) => {
  const { start, end } = getPrevDateRange(period);
  return orders.filter(o => {
    const d = parseOrderDate(o.orderdate || o.created_at);
    if (!d) return false;
    return d >= start && d <= end;
  });
};

const calcTrend = (current, prev) => {
  if (prev === 0 && current === 0) return { status: null, pct: 0 };
  if (prev === 0) return { status: "Increase", pct: 100 };
  const pct = (((current - prev) / prev) * 100).toFixed(1);
  return {
    status: pct > 0 ? "Increase" : pct < 0 ? "Decrease" : null,
    pct: Math.abs(pct),
  };
};

/* ═══════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
═══════════════════════════════════════════════ */
const Home = () => {
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("monthly");

  /* ── All orders (loaded once, client-side filtered per period) ── */
  const [allOrders, setAllOrders] = useState([]);
  const [allOrdersLoaded, setAllOrdersLoaded] = useState(false);

  /* ── Other data states ── */
  const [salesChartData, setSalesChartData]   = useState([]);
  const [orderChartData, setOrderChartData]   = useState([]);
  const [appointData, setAppointData]         = useState([]);
  const [revenueData, setRevenueData]         = useState([]);
  const [docStats, setDocStats]               = useState({ total: 0, active: 0 });
  const [productStats, setProductStats]       = useState({ total: 0 });
  const [appointSplit, setAppointSplit]        = useState({ video: 0, inpatient: 0 });
  const [recentOrders, setRecentOrders]        = useState([]);
  const [recentBookings, setRecentBookings]    = useState([]);
  const [topProducts, setTopProducts]          = useState([]);

  const token = localStorage.getItem("authToken");

  const headers = useMemo(() => ({
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  /* ── Compute period stats from allOrders (client-side) ── */
  const periodStats = useMemo(() => {
    if (!allOrdersLoaded) return {
      total_sales: null, total_revenue: null, total_orders: null, new_users: null,
      sales_status: null, sales_change_percentage: null,
      revenue_status: null, revenue_change_percentage: null,
      orders_status: null, orders_change_percentage: null,
      new_users_status: null, new_users_change_percentage: null,
    };

    const current = filterByPeriod(allOrders, period);
    const prev    = filterByPrevPeriod(allOrders, period);

    const sumAmount = (list) => list.reduce((s, o) => s + parseFloat(o.totalamount || 0), 0);
    const paidOnly  = (list) => list.filter(o => o.status === "Paid" || o.status === "paid");

    const curSales   = sumAmount(current);
    const prevSales  = sumAmount(prev);
    const curRev     = sumAmount(paidOnly(current));
    const prevRev    = sumAmount(paidOnly(prev));
    const curOrders  = current.length;
    const prevOrders = prev.length;

    // New users: unique user IDs / emails in period
    const curUsers  = new Set(current.map(o => o.user_id || o.email)).size;
    const prevUsers = new Set(prev.map(o => o.user_id || o.email)).size;

    const salesTrend   = calcTrend(curSales, prevSales);
    const revTrend     = calcTrend(curRev, prevRev);
    const ordersTrend  = calcTrend(curOrders, prevOrders);
    const usersTrend   = calcTrend(curUsers, prevUsers);

    return {
      total_sales:                 curSales.toFixed(2),
      total_revenue:               curRev.toFixed(2),
      total_orders:                curOrders,
      new_users:                   curUsers,
      sales_status:                salesTrend.status,
      sales_change_percentage:     salesTrend.pct,
      revenue_status:              revTrend.status,
      revenue_change_percentage:   revTrend.pct,
      orders_status:               ordersTrend.status,
      orders_change_percentage:    ordersTrend.pct,
      new_users_status:            usersTrend.status,
      new_users_change_percentage: usersTrend.pct,
    };
  }, [allOrders, allOrdersLoaded, period]);

  /* ── Fetch ALL orders (paginate through all pages) ── */
  const fetchAllOrders = useCallback(async () => {
    setLoading(true);
    try {
      // First call to get total count
      const res  = await fetch(`${API_BASE_URL}api/orders?page=1&per_page=100`, { headers });
      const data = await res.json();
      if (!data.status) return;

      const firstPage = data.data?.data ?? data.data ?? [];
      const total     = data.data?.total ?? firstPage.length;
      const perPage   = 100;
      const totalPages = Math.ceil(total / perPage);

      let allData = [...firstPage];

      if (totalPages > 1) {
        const promises = [];
        for (let pg = 2; pg <= totalPages; pg++) {
          promises.push(
            fetch(`${API_BASE_URL}api/orders?page=${pg}&per_page=${perPage}`, { headers })
              .then(r => r.json())
              .then(d => d.data?.data ?? d.data ?? [])
              .catch(() => [])
          );
        }
        const rest = await Promise.all(promises);
        rest.forEach(chunk => { allData = allData.concat(chunk); });
      }

      setAllOrders(allData);
      setAllOrdersLoaded(true);

      // Set recent orders (latest 5)
      const sorted = [...allData].sort((a, b) => {
        const da = parseOrderDate(a.orderdate || a.created_at);
        const db = parseOrderDate(b.orderdate || b.created_at);
        return (db || 0) - (da || 0);
      });
      setRecentOrders(sorted.slice(0, 5));

    } catch (err) {
      console.error("Error fetching all orders:", err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  /* ── Build chart data from allOrders whenever period changes ── */
  useEffect(() => {
    if (!allOrdersLoaded || allOrders.length === 0) return;

    const current = filterByPeriod(allOrders, period);

    // Group by appropriate key
    const groupKey = (dateStr) => {
      const d = parseOrderDate(dateStr);
      if (!d) return "Unknown";
      if (period === "day") {
        return d.toLocaleTimeString("en-IN", { hour: "2-digit", hour12: true });
      } else if (period === "weekly") {
        return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" });
      } else if (period === "monthly") {
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
      } else {
        return d.toLocaleDateString("en-IN", { month: "short" });
      }
    };

    const groupMap = {};
    current.forEach(o => {
      const key = groupKey(o.orderdate || o.created_at);
      if (!groupMap[key]) groupMap[key] = { month: key, total: 0, orders: 0 };
      groupMap[key].total  += parseFloat(o.totalamount || 0);
      groupMap[key].orders += 1;
    });

    const chartArr = Object.values(groupMap);
    setSalesChartData(chartArr);
    setOrderChartData(chartArr);
    // Revenue = paid only
    const revenueMap = {};
    current.filter(o => o.status === "Paid" || o.status === "paid").forEach(o => {
      const key = groupKey(o.orderdate || o.created_at);
      if (!revenueMap[key]) revenueMap[key] = { month: key, total: 0 };
      revenueMap[key].total += parseFloat(o.totalamount || 0);
    });
    setRevenueData(Object.values(revenueMap));

  }, [allOrders, allOrdersLoaded, period]);

  const fetchSalesGraph = useCallback((p) => {
    fetch(`${API_BASE_URL}api/salesSummaryGraph?period=${p}`, { headers })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setSalesChartData(data); })
      .catch(() => {});
  }, [headers]);

  const fetchAppointGraph = useCallback((p) => {
    fetch(`${API_BASE_URL}api/appointmentSummaryGraph?period=${p}`, { headers })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) setAppointData(data);
        else if (data?.data && data.data.length > 0) setAppointData(data.data);
      })
      .catch(() => {});
  }, [headers]);

  const fetchDoctorStats = useCallback(() => {
    fetch(`${API_BASE_URL}api/doctors?per_page=1`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data.pagination) setDocStats({ total: data.pagination.total, active: data.pagination.total });
      })
      .catch(console.error);
  }, [headers]);

  const fetchProductStats = useCallback(() => {
    fetch(`${API_BASE_URL}api/products?per_page=1`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data.pagination) setProductStats({ total: data.pagination.total });
      })
      .catch(console.error);
  }, [headers]);

  const fetchAppointSplit = useCallback(() => {
    fetch(`${API_BASE_URL}api/doctor-bookings?per_page=1000`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data?.data) {
          const video     = data.data.filter(d => d.booking_type === "video" || d.booking_type === "videocall").length;
          const inpatient = data.data.filter(d => d.booking_type !== "video" && d.booking_type !== "videocall").length;
          setAppointSplit({ video, inpatient });
        }
      })
      .catch(console.error);
  }, [headers]);

  const fetchRecentBookings = useCallback(() => {
    fetch(`${API_BASE_URL}api/doctor-bookings?per_page=5`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data?.data) setRecentBookings(data.data.slice(0, 5));
      })
      .catch(console.error);
  }, [headers]);

  const fetchTopProducts = useCallback(() => {
    fetch(`${API_BASE_URL}api/products?per_page=5`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data?.data) setTopProducts(data.data.slice(0, 5));
      })
      .catch(console.error);
  }, [headers]);

  /* ── On mount ── */
  useEffect(() => {
    fetchAllOrders();
    fetchDoctorStats();
    fetchProductStats();
    fetchAppointSplit();
    fetchRecentBookings();
    fetchTopProducts();
    fetchAppointGraph("monthly");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── On period change: reload chart data from API too (fallback already computed above) ── */
  useEffect(() => {
    fetchSalesGraph(period);
    fetchAppointGraph(period);
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Derived ─── */
  const pieData = [
    { name: "Video Call",  value: appointSplit.video },
    { name: "In-Patient",  value: appointSplit.inpatient },
  ];
  const PIE_COLORS = ["#3b82f6", "#10b981"];

  /* ─── Status badge ─── */
  const statusBadge = (status) => {
    const map = {
      Paid:           { bg: "#ecfdf5", color: "#047857" },
      "Un Paid":      { bg: "#fff1f0", color: "#c0392b" },
      Confirmed:      { bg: "#ecfdf5", color: "#047857" },
      Booked:         { bg: "#eff6ff", color: "#1d4ed8" },
      Cancelled:      { bg: "#fff1f0", color: "#c0392b" },
      Completed:      { bg: "#ecfdf5", color: "#047857" },
      Pending:        { bg: "#f4f4f5", color: "#52525b" },
      "Not Attended": { bg: "#fefce8", color: "#a16207" },
    };
    const s = map[status] || { bg: "#f4f4f5", color: "#52525b" };
    return (
      <span style={{ background: s.bg, color: s.color, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
        {status}
      </span>
    );
  };

  const periodLabel = period === "day" ? "Today" : period.charAt(0).toUpperCase() + period.slice(1);

  /* ═══ RENDER ═══ */
  return (
    <>
      <div id="layout-wrapper">
        <Header />
        {loading && <div className="dash-overlay"><div className="dash-spinner" /></div>}

        <div className="main-content">
          <div className="page-content">
            <div className="dash-shell">

              {/* ── Breadcrumb ── */}
              <div style={{ marginBottom: 20 }}>
                <nav className="dash-breadcrumb">
                  <a href="#!"><CiHome style={{ marginRight: 3 }} />Home</a>
                  <span className="sep">/</span>
                  <a href="#!">Dashboard</a>
                  <span className="sep">/</span>
                  <span className="active">Overview</span>
                </nav>
              </div>

              {/* ── Welcome Banner ── */}
              <div className="dash-welcome">
                <div className="dash-welcome__left">
                  <p className="dash-welcome__greeting">Good Day 👋</p>
                  <h2 className="dash-welcome__title">Welcome Back, Admin!</h2>
                  <p className="dash-welcome__subtitle">Here's a quick look at your store's performance. Stay on top of sales, orders, and customers.</p>
                </div>
                <div className="dash-welcome__stats">
                  <div className="dash-welcome__stat">
                    <span className="dash-welcome__stat-val">₹{periodStats.total_sales ?? "—"}</span>
                    <span className="dash-welcome__stat-label">{periodLabel} Sales</span>
                  </div>
                  <div className="dash-welcome__stat">
                    <span className="dash-welcome__stat-val">{periodStats.total_orders ?? "—"}</span>
                    <span className="dash-welcome__stat-label">Total Orders</span>
                  </div>
                  <div className="dash-welcome__stat">
                    <span className="dash-welcome__stat-val">{periodStats.new_users ?? "—"}</span>
                    <span className="dash-welcome__stat-label">New Customers</span>
                  </div>
                </div>
              </div>

              {/* ── Period Filter ── */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                <h4 className="dash-section-title" style={{ margin: 0 }}>
                  <ActivityIcon size={16} style={{ color: "#10b981" }} />
                  Performance Overview
                </h4>
                <PeriodFilter value={period} onChange={setPeriod} />
              </div>

              {/* ── STAT CARDS ROW 1 — Sales & Revenue ── */}
              <div className="dash-stats-grid">
                <StatCard
                  title="Total Sales"
                  value={periodStats.total_sales}
                  subtitle={`${periodLabel} earnings`}
                  icon={<ShoppingCartIcon size={22} />}
                  color="#10b981"
                  link="/orderlist"
                  trend={periodStats.sales_status}
                  trendValue={periodStats.sales_change_percentage}
                />
                <StatCard
                  title="Total Revenue"
                  value={periodStats.total_revenue}
                  subtitle={`${periodLabel} net earnings (Paid)`}
                  icon={<DollarSignIcon size={22} />}
                  color="#06b6d4"
                  link="/orderlist"
                  trend={periodStats.revenue_status}
                  trendValue={periodStats.revenue_change_percentage}
                />
                <StatCard
                  title="Total Orders"
                  value={periodStats.total_orders}
                  subtitle={`${periodLabel} orders`}
                  icon={<ClipboardIcon size={22} />}
                  color="#f59e0b"
                  link="/orderlist"
                  trend={periodStats.orders_status}
                  trendValue={periodStats.orders_change_percentage}
                  prefix=""
                />
                <StatCard
                  title="New Customers"
                  value={periodStats.new_users}
                  subtitle={`${periodLabel} unique customers`}
                  icon={<UsersIcon size={22} />}
                  color="#8b5cf6"
                  trend={periodStats.new_users_status}
                  trendValue={periodStats.new_users_change_percentage}
                  prefix=""
                />
              </div>

              {/* ── STAT CARDS ROW 2 — Appointments & Doctors ── */}
              <div className="dash-stats-grid" style={{ marginBottom: 24 }}>
                <StatCard
                  title="Doctor Appointments"
                  value={appointSplit.video + appointSplit.inpatient}
                  subtitle="Total bookings"
                  icon={<CalendarIcon size={22} />}
                  color="#ec4899"
                  link="/doctor-bookings"
                  prefix=""
                />
                <StatCard
                  title="Video Call Appointments"
                  value={appointSplit.video}
                  subtitle="Total video consultations"
                  icon={<VideoIcon size={22} />}
                  color="#3b82f6"
                  link="/doctor-bookings"
                  prefix=""
                />
                <StatCard
                  title="In-Patient Appointments"
                  value={appointSplit.inpatient}
                  subtitle="Physical consultations"
                  icon={<StethoscopeIcon size={22} />}
                  color="#10b981"
                  link="/doctor-bookings"
                  prefix=""
                />
                <StatCard
                  title="Total Doctors"
                  value={docStats.total}
                  subtitle="Registered doctors"
                  icon={<UsersIcon size={22} />}
                  color="#14b8a6"
                  link="/doctor"
                  prefix=""
                />
                <StatCard
                  title="Total Products"
                  value={productStats.total}
                  subtitle="In catalogue"
                  icon={<PackageIcon size={22} />}
                  color="#f97316"
                  link="/product"
                  prefix=""
                />
              </div>

              {/* ── Charts ── */}
              <p className="dash-section-title"><TrendingUpIcon size={15} />Sales & Revenue Charts</p>

              {/* Sales Summary + Pie */}
              <div className="dash-charts-row cols-3" style={{ marginBottom: 20 }}>
                <ChartCard
                  title="Sales Summary"
                  subtitle={`${periodLabel} sales trend`}
                  action={
                    <Link to="/orderlist" className="dash-table-card__link">
                      View all <ArrowRightIcon size={13} />
                    </Link>
                  }
                >
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={salesChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#10b981" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f6f3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="total" name="Sales" stroke="#10b981" strokeWidth={2.5} fill="url(#salesGrad)" dot={{ r: 4, fill: "#10b981", strokeWidth: 0 }} activeDot={{ r: 6 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                  title="Appointment Split"
                  subtitle="Video vs In-Patient"
                  action={
                    <Link to="/doctor-bookings" className="dash-table-card__link">
                      View all <ArrowRightIcon size={13} />
                    </Link>
                  }
                >
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="45%" outerRadius={90} dataKey="value"
                        labelLine={false} label={<PieLabel />}>
                        {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 13, paddingTop: 10 }} />
                      <Tooltip formatter={(v) => [v, ""]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="dash-mini-strip" style={{ marginTop: 8 }}>
                    <div className="dash-mini-item">
                      <div className="dash-mini-item__icon" style={{ background: "#eff6ff", color: "#3b82f6" }}>
                        <VideoIcon size={18} />
                      </div>
                      <div>
                        <p className="dash-mini-item__label">Video Call</p>
                        <p className="dash-mini-item__val">{appointSplit.video}</p>
                      </div>
                    </div>
                    <div className="dash-mini-item">
                      <div className="dash-mini-item__icon" style={{ background: "#ecfdf5", color: "#10b981" }}>
                        <PhoneIcon size={18} />
                      </div>
                      <div>
                        <p className="dash-mini-item__label">In-Patient</p>
                        <p className="dash-mini-item__val">{appointSplit.inpatient}</p>
                      </div>
                    </div>
                  </div>
                </ChartCard>
              </div>

              {/* Orders Bar + Appointments Line */}
              <div className="dash-charts-row cols-2" style={{ marginBottom: 24 }}>
                <ChartCard
                  title="Orders Overview"
                  subtitle={`${periodLabel} order volumes`}
                  action={
                    <Link to="/orderlist" className="dash-table-card__link">
                      Orders <ArrowRightIcon size={13} />
                    </Link>
                  }
                >
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={orderChartData.length ? orderChartData : salesChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f6f3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total" name="Orders" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                  title="Doctor Appointments"
                  subtitle={`${periodLabel} appointment trend`}
                  action={
                    <Link to="/doctor-bookings" className="dash-table-card__link">
                      Bookings <ArrowRightIcon size={13} />
                    </Link>
                  }
                >
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={appointData.length ? appointData : salesChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="apptGrad" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%"   stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f6f3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="total" name="Appointments" stroke="url(#apptGrad)" strokeWidth={2.5} dot={{ r: 4, fill: "#3b82f6", strokeWidth: 0 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              {/* Revenue Area Chart */}
              <div style={{ marginBottom: 24 }}>
                <ChartCard
                  title="Revenue Breakdown"
                  subtitle={`Paid orders revenue — ${periodLabel}`}
                  action={<Link to="/orderlist" className="dash-table-card__link">Full Report <ArrowRightIcon size={13} /></Link>}
                >
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={revenueData.length ? revenueData : salesChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="revGrad1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#06b6d4" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f6f3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#6b7a74" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="total" name="Revenue" stroke="#06b6d4" strokeWidth={2} fill="url(#revGrad1)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              {/* ── Bottom Tables ── */}
              <p className="dash-section-title"><ClipboardIcon size={15} />Recent Activity</p>
              <div className="dash-charts-row cols-2" style={{ marginBottom: 24 }}>

                {/* Recent Orders */}
                <div className="dash-table-card">
                  <div className="dash-table-card__header">
                    <h4 className="dash-table-card__title">Recent Orders</h4>
                    <Link to="/orderlist" className="dash-table-card__link">
                      View All <ArrowRightIcon size={13} />
                    </Link>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentOrders.length === 0 ? (
                          <tr><td colSpan={4} style={{ textAlign: "center", color: "#9aa8a2", padding: 24 }}>No orders found</td></tr>
                        ) : recentOrders.map(o => (
                          <tr key={o.orderid}>
                            <td>
                              <span style={{ fontFamily: "monospace", background: "#eef4f1", padding: "3px 9px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                                #{o.orderid}
                              </span>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{o.name}</div>
                              <div style={{ fontSize: 12, color: "#9aa8a2" }}>{o.email}</div>
                            </td>
                            <td style={{ fontWeight: 700, color: "#059669" }}>₹{o.totalamount}</td>
                            <td>{statusBadge(o.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Bookings */}
                <div className="dash-table-card">
                  <div className="dash-table-card__header">
                    <h4 className="dash-table-card__title">Recent Appointments</h4>
                    <Link to="/doctor-bookings" className="dash-table-card__link">
                      View All <ArrowRightIcon size={13} />
                    </Link>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Doctor</th>
                          <th>Patient</th>
                          <th>Type</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentBookings.length === 0 ? (
                          <tr><td colSpan={4} style={{ textAlign: "center", color: "#9aa8a2", padding: 24 }}>No bookings found</td></tr>
                        ) : recentBookings.map(b => (
                          <tr key={b.id}>
                            <td style={{ fontWeight: 600, fontSize: 13.5 }}>{b.doctor_data?.name || "—"}</td>
                            <td>{b.patient_name || "—"}</td>
                            <td>
                              {(b.booking_type === "video" || b.booking_type === "videocall") ? (
                                <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "3px 9px", borderRadius: 999, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <VideoIcon size={11} /> Video
                                </span>
                              ) : (
                                <span style={{ background: "#ecfdf5", color: "#047857", padding: "3px 9px", borderRadius: 999, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <PhoneIcon size={11} /> In-Patient
                                </span>
                              )}
                            </td>
                            <td>{statusBadge(b.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* ── Top Products + Quick Stats ── */}
              <div className="dash-charts-row cols-3" style={{ marginBottom: 24 }}>
                <div className="dash-table-card">
                  <div className="dash-table-card__header">
                    <h4 className="dash-table-card__title">Top Products</h4>
                    <Link to="/product" className="dash-table-card__link">
                      All Products <ArrowRightIcon size={13} />
                    </Link>
                  </div>
                  <div>
                    {topProducts.length === 0 ? (
                      <p style={{ textAlign: "center", color: "#9aa8a2", padding: 24, margin: 0 }}>No products found</p>
                    ) : topProducts.map((p, i) => {
                      const imgs = (() => { try { return JSON.parse(p.images); } catch { return [p.images]; } })();
                      const img = imgs?.[0];
                      return (
                        <div key={p.id} className="dash-product-row">
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#9aa8a2", width: 20 }}>{i + 1}</span>
                          <img
                            src={img ? `${API_BASE_URL}public/productimages/${img}` : `${API_BASE_URL}public/noimage.jpg`}
                            alt={p.name}
                            className="dash-product-img"
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="dash-product-name" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                            {p.brand && <div className="dash-product-brand">{p.brand}</div>}
                          </div>
                          <span className="dash-product-price">₹{parseFloat(p.price || 0).toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Summary */}
                <div className="dash-chart-card">
                  <div className="dash-chart-card__header">
                    <div>
                      <h4 className="dash-chart-card__title">Quick Summary</h4>
                      <p className="dash-chart-card__sub">{periodLabel} metrics at a glance</p>
                    </div>
                  </div>
                  <div className="dash-chart-card__body">
                    {[
                      { label: "Total Sales",            val: `₹${periodStats.total_sales   ?? 0}`, color: "#10b981", icon: <ShoppingCartIcon size={16} /> },
                      { label: "Total Revenue",          val: `₹${periodStats.total_revenue ?? 0}`, color: "#06b6d4", icon: <DollarSignIcon    size={16} /> },
                      { label: "Total Orders",           val: periodStats.total_orders       ?? 0,   color: "#f59e0b", icon: <ClipboardIcon     size={16} /> },
                      { label: "Video Appointments",     val: appointSplit.video,                    color: "#3b82f6", icon: <VideoIcon         size={16} /> },
                      { label: "In-Patient Appt.",       val: appointSplit.inpatient,                color: "#10b981", icon: <StethoscopeIcon   size={16} /> },
                      { label: "Total Doctors",          val: docStats.total,                        color: "#8b5cf6", icon: <UsersIcon         size={16} /> },
                      { label: "Total Products",         val: productStats.total,                    color: "#f97316", icon: <PackageIcon       size={16} /> },
                      { label: "New Customers",          val: periodStats.new_users          ?? 0,   color: "#ec4899", icon: <UsersIcon         size={16} /> },
                    ].map((item, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "10px 0", borderBottom: i < 7 ? "1px solid #f0f6f3" : "none"
                      }}>
                        <div style={{ width: 32, height: 32, borderRadius: 9, background: `${item.color}18`, color: item.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {item.icon}
                        </div>
                        <span style={{ fontSize: 13.5, color: "#4b5b56", flex: 1 }}>{item.label}</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.val}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <Link to="/orderlist" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: "linear-gradient(135deg,#14b8a6,#10b981)", color: "#fff", borderRadius: 10, fontWeight: 600, fontSize: 13, textDecoration: "none", boxShadow: "0 4px 12px rgba(16,185,129,.25)" }}>
                        <ClipboardIcon size={14} /> Orders
                      </Link>
                      <Link to="/doctor-bookings" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 10, fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                        <CalendarIcon size={14} /> Bookings
                      </Link>
                      <Link to="/doctor" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: 10, fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                        <StethoscopeIcon size={14} /> Doctors
                      </Link>
                      <Link to="/product" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 14px", background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa", borderRadius: 10, fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                        <PackageIcon size={14} /> Products
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scroll-to-top progress */}
              <div className="progress-wrap" id="progress-scroll">
                <svg className="progress-circle" width="100%" height="100%" viewBox="-1 -1 102 102">
                  <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
                </svg>
              </div>

            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default Home;